import React, { useState, useEffect } from 'react'
import { listDeliveryAuditLogs, formatApiErrorMessage } from '../api/emailSettingsApi'

export default function DeliveryAuditLogsTab() {
  const [logs, setLogs] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = 15
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedLog, setSelectedLog] = useState(null)
  const [feedback, setFeedback] = useState(null)

  useEffect(() => {
    loadLogs()
  }, [page, statusFilter])

  async function loadLogs() {
    setLoading(true)
    setFeedback(null)
    try {
      const params = {
        page,
        pageSize,
        channel: 'EMAIL',
      }
      if (statusFilter !== 'ALL') {
        params.status = statusFilter
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim()
      }

      const res = await listDeliveryAuditLogs(params)
      const list = res?.rows || res?.data || res?.items || (Array.isArray(res) ? res : [])
      const count = Number(
        res?.totalCount ??
        res?.total_count ??
        res?.pagination?.total_count ??
        res?.count ??
        (Array.isArray(list) ? list.length : 0)
      )
      setLogs(Array.isArray(list) ? list : [])
      setTotalCount(count)
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Failed to load delivery logs: ' + formatApiErrorMessage(err),
      })
    } finally {
      setLoading(false)
    }
  }

  function handleSearchSubmit(e) {
    e.preventDefault()
    setPage(1)
    loadLogs()
  }

  const totalPages = Math.ceil(totalCount / pageSize) || 1

  return (
    <div className="flex flex-col gap-6">
      {/* ── Top Canopy: Header & Action ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/15 pb-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            SYSTEM &amp; COMMUNICATIONS
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Outgoing Delivery Audit Logs
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Immutable audit record of all automated email dispatches, escalations, delivery timestamps, and failure diagnostics.
          </p>
        </div>

        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition self-start sm:self-auto"
          onClick={() => loadLogs()}
          disabled={loading}
        >
          <span
            className={`material-symbols-outlined text-sm ${loading ? 'animate-spin' : ''}`}
          >
            refresh
          </span>
          Refresh Logs
        </button>
      </section>

      {/* ── Main Elevated Card: Filter Bar & Table ── */}
      <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs">

      {feedback && (
        <div className="flex items-center gap-2.5 p-3.5 mb-5 rounded-2xl text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-200">
          <span className="material-symbols-outlined text-base">error</span>
          <span>{feedback.message}</span>
        </div>
      )}

      {/* Filter and Search Bar */}
      <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-1.5">
          {['ALL', 'SENT', 'FAILED'].map((st) => {
            const isActive = statusFilter === st
            return (
              <button
                key={st}
                type="button"
                onClick={() => {
                  setStatusFilter(st)
                  setPage(1)
                }}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${
                  isActive
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'border border-outline-variant/30 bg-surface-container-low text-on-surface hover:bg-surface-container'
                }`}
              >
                {st}
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-2 flex-1 md:max-w-md">
          <div className="relative flex-1">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-sm text-on-surface-variant/60 pointer-events-none">
              search
            </span>
            <input
              type="text"
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low pl-9 pr-3.5 py-1.5 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary placeholder:text-on-surface-variant/40"
              placeholder="Search by recipient email or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-1 rounded-xl bg-primary px-3.5 py-1.5 text-xs font-bold text-on-primary shadow-xs transition hover:brightness-110 active:scale-[0.98]"
          >
            Search
          </button>
        </div>
      </form>

      {/* Logs Table / Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center p-14 rounded-2xl border border-outline-variant/20 bg-surface-container-low">
          <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
          <p className="mt-3 text-xs text-on-surface-variant font-medium">Querying communication audit logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 rounded-2xl border border-dashed border-outline-variant/40 bg-surface-container-low text-center">
          <span className="material-symbols-outlined text-4xl text-on-surface-variant/50">
            mark_email_unread
          </span>
          <p className="mt-2 text-xs font-bold text-on-surface">No outbound delivery logs match your filter.</p>
          <p className="text-[11px] text-on-surface-variant mt-0.5">
            Outgoing email audit events will be automatically tracked and logged here.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-outline-variant/20 bg-surface-container-lowest">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-surface-container-low/75 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant border-b border-outline-variant/20">
                <th className="px-4 py-3 font-extrabold w-[16%]">Dispatched At</th>
                <th className="px-4 py-3 font-extrabold w-[20%]">Recipient</th>
                <th className="px-4 py-3 font-extrabold w-[16%]">Message Type</th>
                <th className="px-4 py-3 font-extrabold w-[28%]">Subject</th>
                <th className="px-4 py-3 font-extrabold w-[10%]">Status</th>
                <th className="px-4 py-3 font-extrabold w-[10%] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {logs.map((log) => {
                const isSent = String(log.status || '').toUpperCase() === 'SENT'
                const displayDate = log.sent_at || log.created_at || log.sentAt || log.createdAt
                return (
                  <tr key={log.id} className="hover:bg-surface-container-low/50 transition">
                    <td className="px-4 py-3 text-on-surface-variant font-mono text-[11px] whitespace-nowrap">
                      {displayDate ? new Date(displayDate).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 font-bold text-on-surface">
                      {log.recipient}
                    </td>
                    <td className="px-4 py-3 font-mono text-primary text-[11px]">
                      {log.message_type || log.messageType || 'GENERAL'}
                    </td>
                    <td className="px-4 py-3 text-on-surface-variant truncate max-w-[240px]" title={log.subject}>
                      {log.subject || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold ${
                          isSent
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}
                      >
                        {isSent ? '✓ SENT' : '✕ FAILED'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-lg border border-outline-variant/30 bg-surface-container-low px-2.5 py-1 text-[11px] font-bold text-on-surface hover:bg-surface-container-high transition"
                        onClick={() => setSelectedLog(log)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 text-xs text-on-surface-variant">
        <div>
          Showing page <strong className="text-on-surface">{page}</strong> of{' '}
          <strong className="text-on-surface">{totalPages}</strong> ({totalCount} total events)
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-1.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-1.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-150"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="w-full max-w-2xl rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-2xl flex flex-col gap-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <span className="material-symbols-outlined text-xl">receipt_long</span>
                </span>
                <div>
                  <h3 className="font-display text-base font-extrabold text-on-surface">
                    Delivery Log Details #{selectedLog.id}
                  </h3>
                  <p className="text-[11px] text-on-surface-variant font-mono">
                    Dispatched:{' '}
                    {selectedLog.sent_at || selectedLog.created_at
                      ? new Date(selectedLog.sent_at || selectedLog.created_at).toLocaleString()
                      : '—'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-container-high transition"
                onClick={() => setSelectedLog(null)}
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>

            <div className="flex flex-col gap-4 text-xs">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                  Subject Line
                </label>
                <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low px-3.5 py-2.5 font-bold text-on-surface">
                  {selectedLog.subject || '—'}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                    Recipient
                  </label>
                  <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low px-3.5 py-2 font-mono text-on-surface">
                    {selectedLog.recipient}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                    Sender
                  </label>
                  <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low px-3.5 py-2 font-mono text-on-surface">
                    {selectedLog.sender || 'System Default Mailbox'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                    Message Type
                  </label>
                  <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low px-3.5 py-2 font-mono text-primary font-bold">
                    {selectedLog.message_type || selectedLog.messageType}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                    Delivery Status
                  </label>
                  <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low px-3.5 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold ${
                        String(selectedLog.status).toUpperCase() === 'SENT'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-rose-50 text-rose-700 border border-rose-200'
                      }`}
                    >
                      {selectedLog.status}
                    </span>
                  </div>
                </div>
              </div>

              {selectedLog.error_message && (
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold text-rose-600 uppercase tracking-wider">
                    Failure Diagnostics / SMTP Error
                  </label>
                  <pre className="rounded-xl border border-rose-200 bg-rose-50/75 p-3 font-mono text-[11px] text-rose-900 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {selectedLog.error_message}
                  </pre>
                </div>
              )}

              {selectedLog.body_preview && (
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                    Rendered Body Preview
                  </label>
                  <pre className="rounded-xl border border-outline-variant/20 bg-surface-container-low p-3 font-mono text-[11px] text-on-surface-variant whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {selectedLog.body_preview}
                  </pre>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-3 border-t border-outline-variant/15">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition"
                onClick={() => setSelectedLog(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}


